<script lang="ts">
  // Governance view per INTERFACE-SPECIFICATION.md §10.
  //
  // Seven sub-panels routed under /governance/{panel}:
  //   authority-register   — §10.1
  //   rule-book            — §10.2
  //   substantiation-index — §10.3
  //   compliance-reports   — §10.4
  //   audit                — §10.5 (also reachable with ?hash=)
  //   lifecycle-status     — §10.6
  //   scenarios            — §10.7
  //
  // Each sub-panel is its own URL so a risk officer can share a link
  // in a compliance document or email.

  import { route, navigate } from '../lib/router.svelte';
  import AuthorityRegister from '../components/governance/AuthorityRegister.svelte';
  import RuleBook from '../components/governance/RuleBook.svelte';
  import SubstantiationIndex from '../components/governance/SubstantiationIndex.svelte';
  import ComplianceReports from '../components/governance/ComplianceReports.svelte';
  import AuditByHash from '../components/governance/AuditByHash.svelte';
  import LifecycleStatus from '../components/governance/LifecycleStatus.svelte';
  import ScenariosIndex from '../components/governance/ScenariosIndex.svelte';

  const panels = [
    { key: 'authority-register', label: 'Authority Register' },
    { key: 'rule-book', label: 'Rule Book' },
    { key: 'substantiation-index', label: 'Substantiation Index' },
    { key: 'compliance-reports', label: 'Compliance Reports' },
    { key: 'audit', label: 'Audit by Hash' },
    { key: 'lifecycle-status', label: 'Lifecycle Status' },
    { key: 'scenarios', label: 'Scenarios Index' }
  ];

  const panel = $derived(route.current.params.panel ?? 'authority-register');
</script>

<section class="governance-shell">
  <aside class="governance-rail" aria-label="Governance sub-panels">
    <ul>
      {#each panels as p (p.key)}
        <li>
          <a href={`/governance/${p.key}`} class:is-active={panel === p.key}>{p.label}</a>
        </li>
      {/each}
    </ul>
  </aside>

  <div class="governance-content">
    {#if panel === 'authority-register'}
      <AuthorityRegister />
    {:else if panel === 'rule-book'}
      <RuleBook />
    {:else if panel === 'substantiation-index'}
      <SubstantiationIndex />
    {:else if panel === 'compliance-reports'}
      <ComplianceReports />
    {:else if panel === 'audit'}
      <AuditByHash />
    {:else if panel === 'lifecycle-status'}
      <LifecycleStatus />
    {:else if panel === 'scenarios'}
      <ScenariosIndex />
    {:else}
      <p class="muted">Unknown panel: {panel}</p>
    {/if}
  </div>
</section>

<style>
  .governance-shell {
    display: grid;
    grid-template-columns: 220px 1fr;
    gap: var(--space-5);
  }
  @media (max-width: 900px) {
    .governance-shell { grid-template-columns: 1fr; }
  }
  .governance-rail ul { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: var(--space-1); }
  .governance-rail a {
    display: block;
    padding: var(--space-1) var(--space-2);
    color: var(--surface-muted);
    text-decoration: none;
    border-radius: var(--radius-sm);
  }
  .governance-rail a:hover { background: var(--surface-overlay); color: var(--surface-fg); }
  .governance-rail a.is-active { background: var(--surface-fg); color: var(--action-primary-fg); }

  /* Mobile: the rail becomes a horizontal scroll-strip above the
   * panel content rather than a vertical column that pushes panels
   * off-screen. Touch-friendly hit targets via the 44 px minimum. */
  @media (max-width: 768px) {
    .governance-rail ul {
      flex-direction: row;
      overflow-x: auto;
      scroll-snap-type: x mandatory;
      -webkit-overflow-scrolling: touch;
      gap: var(--space-2);
      padding-bottom: var(--space-1);
    }
    .governance-rail a {
      white-space: nowrap;
      scroll-snap-align: start;
      min-height: var(--touch-target-min);
      display: inline-flex;
      align-items: center;
    }
  }
</style>
